import { motion } from "framer-motion";
import { Zap } from "lucide-react";

interface Milestone {
  count: number;
  label: string;
  description: string;
}

interface VoiceMilestoneProgressProps {
  sampleCount: number;
  milestones: Milestone[];
}

export function VoiceMilestoneProgress({ sampleCount, milestones }: VoiceMilestoneProgressProps) {
  const nextMilestone = milestones.find((m) => sampleCount < m.count);
  const prevMilestone = milestones.filter((m) => sampleCount >= m.count).pop();

  if (!nextMilestone) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-4 space-y-1.5"
      >
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-emerald-400" />
          <p className="text-xs font-semibold text-emerald-400">Maximum Voice Fidelity</p>
        </div>
        <p className="text-xs text-muted-foreground">Your AI twin is fully calibrated with {sampleCount} samples.</p>
      </motion.div>
    );
  }

  const rangeStart = prevMilestone?.count ?? 0;
  const rangeEnd = nextMilestone.count;
  const progress = Math.min(1, (sampleCount - rangeStart) / (rangeEnd - rangeStart));
  const remaining = nextMilestone.count - sampleCount;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border bg-card p-4 space-y-3"
    >
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-primary shrink-0" />
        <p className="text-xs font-semibold text-foreground">Next Unlock</p>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-foreground">{nextMilestone.label}</span>
          <span className="text-muted-foreground tabular-nums">{sampleCount} / {nextMilestone.count}</span>
        </div>
        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-primary to-accent"
            initial={{ width: 0 }}
            animate={{ width: `${progress * 100}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
        <p className="text-[10px] text-muted-foreground">
          {remaining} more sample{remaining !== 1 ? "s" : ""} to unlock
        </p>
      </div>

      <div className="flex gap-1 flex-wrap">
        {milestones.map((m) => {
          const reached = sampleCount >= m.count;
          const isNext = m === nextMilestone;
          return (
            <div
              key={m.count}
              title={`${m.label} — ${m.count} samples`}
              className={`h-1.5 flex-1 rounded-full transition-all ${
                reached
                  ? "bg-emerald-400"
                  : isNext
                  ? "bg-primary/40"
                  : "bg-secondary"
              }`}
            />
          );
        })}
      </div>

      <div className="flex flex-wrap gap-1">
        {milestones.map((m) => {
          const reached = sampleCount >= m.count;
          return (
            <span
              key={m.count}
              title={m.label}
              className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                reached
                  ? "bg-emerald-400/15 text-emerald-400"
                  : "bg-secondary text-muted-foreground"
              }`}
            >
              {m.count}
            </span>
          );
        })}
      </div>
    </motion.div>
  );
}
